import { useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import * as m from 'framer-motion/m'
import { Container } from './Container'

const ABOUT_FEATURE_IMAGE = '/assets/about-feature.webp'

const PILLARS = [
  { title: 'Capital Strength', content: 'Backed by strong financial resources, Impact are strategically positioned to move quickly and decisively to secure the right opportunities.' },
  { title: 'Investment Discipline', content: 'Every opportunity is underpinned by rigorous financial analysis and strategic foresight.' },
  { title: 'Delivery Excellence', content: 'From acquisition to execution, our team brings a proven ability to deliver results.' },
] as const

export function About() {
  const [openPillar, setOpenPillar] = useState<number | null>(0)

  return (
    <section className="bg-[#1e232b] border-t border-white/10" id="about">
      <Container>
        <m.div className="py-12 sm:py-16 md:py-20 w-full" initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
          <div className="w-full max-w-2xl md:max-w-3xl mx-auto">
            <div className="relative w-full rounded overflow-hidden bg-[#1e232b] aspect-square">
              <img
                src={ABOUT_FEATURE_IMAGE}
                alt="Impact Capital"
                className="absolute inset-0 w-full h-full object-cover object-center"
                width={1024}
                height={1024}
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </m.div>
        <div className="bg-no-repeat bg-contain bg-[length:80%_auto] sm:bg-[length:auto] md:bg-contain min-h-[420px] md:min-h-[380px]" style={{ backgroundImage: 'url(/assets/emblem-large-scaled.webp)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            <m.div className="pt-4 md:pt-[6vw] min-h-[2.5em]" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4 }}>
              <h2 className="uppercase text-[#cab6a1] text-[28px] sm:text-[36px] md:text-[48px] leading-[1.2em] font-serif">About Us</h2>
            </m.div>
            <m.div className="text-white/90 leading-[1.8em] text-sm sm:text-[16px] font-sans min-h-[320px]" initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p>
                Impact Capital is a privately held real estate investment and development company with an established track record and ongoing projects across residential and commercial sectors UK-wide — underpinned by a strong pipeline and hands-on delivery approach.
              </p>
              <p className="sr-only">
                Further reading: <a href="https://www.rics.org/" target="_blank" rel="noopener noreferrer">Royal Institution of Chartered Surveyors (RICS)</a>.
              </p>
              <p className="mt-4">
                Our team combines deep sector experience with an entrepreneurial mindset, enabling us to act decisively, structure complex transactions, and deliver projects efficiently. Impact invest both independently and in partnership to create sustainable, value-driven outcomes. Learn more about <a href="#approach" className="text-[#cab6a1] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cab6a1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e232b] rounded">our approach</a> or <a href="#contact" className="text-[#cab6a1] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cab6a1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e232b] rounded">get in touch</a>.
              </p>
              <p className="mt-4">Our foundation is built on three pillars:</p>
              <div className="mt-4 space-y-2 min-h-[380px]">
                {PILLARS.map((p, i) => {
                  const isOpen = openPillar === i
                  const panelId = `pillar-panel-${i}`
                  const buttonId = `pillar-button-${i}`
                  return (
                    <div key={i} className="border border-white/15 rounded overflow-hidden">
                      <button
                        id={buttonId}
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={() => setOpenPillar(isOpen ? null : i)}
                        className="w-full text-left px-4 py-3 min-h-[48px] flex items-center justify-between bg-white/10 text-white font-serif font-medium uppercase text-sm hover:bg-white/15 touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-[#cab6a1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e232b]"
                      >
                        {p.title}
                        <span className="text-white/70" aria-hidden>{isOpen ? '−' : '+'}</span>
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <m.div
                            id={panelId}
                            role="region"
                            aria-labelledby={buttonId}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="overflow-hidden px-4 py-3 text-white/90 text-[15px] leading-[1.7] border-t border-white/10 bg-white/5 font-sans"
                          >
                            {p.content}
                          </m.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>
            </m.div>
          </div>
        </div>
      </Container>
    </section>
  )
}
